import {createAction, ActionCreator} from '@packages/utilities/create-action';
import {
  CONTENT_EDITOR_EMBED_TEXT_SAVED,
  CONTENT_EDITOR_FORMAT_TOGGLED,
  CONTENT_EDITOR_CHARS_INSERTED,
  CONTENT_EDITOR_SELECTION_UPDATED,
  CONTENT_EDITOR_MOUSE_DOWN,
  CONTENT_EDITOR_MOUSE_UP,
  CONTENT_EDITOR_CUT,
  CONTENT_EDITOR_PASTE,
  CONTENT_EDITOR_KEY_DOWN,
  CONTENT_EDITOR_KEYBOARD_SHORTCUT_PRESSED,
  CONTENT_EDITOR_TEXT_SHORTCUT_ACTIVATED,
  CONTENT_EDITOR_FIGURE_SETTINGS_OPENED,
  CONTENT_EDITOR_FIGURE_ALIGNMENT_CHANGED,
  CONTENT_EDITOR_FIGURE_ALIGNED_HEIGHT_UPDATED_FROM_SETTINGS,
  CONTENT_EDITOR_FIGURE_ALT_TEXT_UPDATED,
  CONTENT_EDITOR_FIGURE_MAX_WIDTH_UPDATED,
  CONTENT_EDITOR_FIGURE_MAX_WIDTH_HEIGHT_UPDATED,
  CONTENT_EDITOR_FIGURE_LINK_UPDATED,
  CONTENT_EDITOR_LINK_CANCELLED,
  CONTENT_EDITOR_LINK_REMOVED,
  CONTENT_EDITOR_LINK_ACCEPTED,
  CONTENT_EDITOR_LINK_TOOLBAR_SAVED,
  CONTENT_EDITOR_VIDEO_CANCELLED,
  CONTENT_EDITOR_IMAGE_UPLOAD_PROGRESS_CHANGED,
  CONTENT_EDITOR_IMAGE_PREVIEW_SET,
  CONTENT_EDITOR_IMAGE_UPLOAD_FINISHED,
  CONTENT_EDITOR_IMAGE_UPLOAD_ERRORED,
  CONTENT_EDITOR_IMAGE_UPLOAD_FAILED,
  CONTENT_EDITOR_IMAGE_REPLACEMENT_STARTED,
  CONTENT_EDITOR_IMAGE_REPLACEMENT_SUCCEEDED,
  CONTENT_EDITOR_IMAGE_REPLACEMENT_FAILED,
  CONTENT_EDITOR_IMAGE_SPINNER_POSITIONS_CHANGED,
  CONTENT_EDITOR_IMAGE_IMPORT_STARTED,
  CONTENT_EDITOR_IMAGE_IMPORT_FINISHED,
  CONTENT_EDITOR_IMAGE_IMPORT_FAILED,
  CONTENT_EDITOR_LINK_MODE_STARTED,
  CONTENT_EDITOR_REDO,
  CONTENT_EDITOR_UNDO,
  CONTENT_EDITOR_TEXT_VALUE_REPLACED,
  CONTENT_EDITOR_LINK_TOOLBAR_CHANGES_MADE,
  CONTENT_EDITOR_SELECTION_RECT_UPDATED,
  CONTENT_EDITOR_CUSTOM_CLASS_ACCEPTED,
  CONTENT_EDITOR_CUSTOM_CLASS_CANCELLED,
  CONTENT_EDITOR_CUSTOM_CLASS_REMOVED,
  TEXT_OF_NODE_SELECTED_IN_INSTANCE_EDITING_ENDED,
  CONTENT_EDITOR_MARKDOWN_FORMATTING_ACTIVATED,
  CONTENT_EDITOR_CODE_BLOCK_SETTINGS_OPENED,
  CONTENT_EDITOR_COMPONENT_SETTINGS_OPENED,
  DYN_FIELD_MODIFIED_ON_CANVAS,
  CONTENT_EDITOR_VALIDATION_ERROR_SHOWN,
  CONTENT_EDITOR_DISCARD_MODAL_OPENED,
  CONTENT_EDITOR_DISCARD_MODAL_CLOSED,
  DYN_FIELD_SAVE_ON_CANVAS_STARTED,
  DYN_FIELD_SAVE_ON_CANVAS_SUCCESS,
  DYN_FIELD_SAVE_ON_CANVAS_ERROR,
  DYN_FIELD_SAVE_ON_CANVAS_DISCARDED,
  RICH_TEXT_CONTENT_MODAL_EDIT_STARTED,
} from '@packages/systems/designer/actions';
import type {Box} from '@packages/utilities/box';
import type {
  ParsedSelectionType,
  LinkToolbarChanges,
} from '@packages/shared/types/ContentEditor';
import type {SelectionRecordType} from '@packages/shared/records';
import type {CollectionItem} from '@packages/systems/cms/types';
import type {FieldType} from '@packages/systems/dynamo/records/Field';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  ENTER,
  SPACE,
  BACKSPACE,
  DELETE,
  LEFT,
  UP,
  RIGHT,
  DOWN,
} from '@packages/utilities/key-utils';
import type {FormatButton} from './constants';
import type {OnCanvasSaveErrorPayload} from './types';

type SeedId = string;

// Payload types
export type SelectionUpdatedPayload = {
  parsedSelection: ParsedSelectionType;
};

export type FormatToggledPayload = {
  seedId: SeedId;
  format: FormatButton;
};

type FigureLinkUpdatedPayload = {
  seedId: SeedId;
  href: string;
  figureId: string;
};

export type CharsInsertedPayload = {
  seedId: SeedId;
  chars: string;
};

type MouseDownPayload = {
  seedId: SeedId;
  parsedSelection: ParsedSelectionType;
};

export type MouseUpPayload = {
  parsedSelection: ParsedSelectionType;
};

export type PastePayload = {
  subtree: ExpressionElement;
  selection: SelectionRecordType;
};

export type KeyDownPayload =
  | {
      key: typeof SPACE;
      seedId: SeedId;
      consecutiveSpacebarAttempts: number;
    }
  | {key: typeof ENTER; seedId: SeedId}
  | {key: typeof BACKSPACE; seedId: SeedId}
  | {key: typeof DELETE; seedId: SeedId}
  | {key: typeof LEFT; seedId: SeedId}
  | {key: typeof RIGHT; seedId: SeedId}
  | {key: typeof UP; seedId: SeedId}
  | {key: typeof DOWN; seedId: SeedId};

export type KeyboardShortcutPressedPayload = {
  seedId: SeedId;
  keyboardShortcut: string;
};

type TextShortcutActivatedPayload = {
  seedId: SeedId;
  textShortcut: string;
};

export type MarkdownFormattingActivatedPayload = {
  seedId: SeedId;
  key: number;
};

export type FigureAlignmentChangedPayload = {
  alignment: string;
  figureNodeId?: string;
  maxWidth?: string;
};

export type FigureAlignedAndHeightUpdatedFromSettingsPayload = {
  position: string;
  maxHeight: string;
};

type AltTextUpdatedPayload = {
  value: string;
};

type MaxWidthUpdatedPayload = {
  value: string;
};

export type MaxWidthHeightUpdatedPayload = {
  maxWidth: string;
  maxHeight: string;
};

export type LinkRemovedPayload = {
  seedId: SeedId;
  linkNodeId: string;
};

export type CustomClassAcceptedPayload = {
  customClass: string;
};

export type ImgNodeRectsType = {
  [key: string]: Box;
};

export type ImageUploadProgressChangedPayload = {
  imageFigureId: string;
  progress: number;
};

export type ImagePreviewSetPayload = {
  imageFigureId: string;
  blobUrl: string;
};

export type ImageUploadFinishedPayload = {
  imageFigureId: string;
  cdnUrl: string;
  assetId: string;
};

export type ImageUploadedWithErrorPayload = {
  imageFigureId: string;
};

export type ImageUploadFailedPayload = {
  seedId: SeedId;
  imageFigureId: string;
};

export type ImageReplacementStartedPayload = {
  imageFigureId: string;
};

export type ImageReplacementSucceededPayload = {
  imageFigureId: string;
  cdnUrl: string;
  assetId: string;
};

export type ImageReplacementFailedPayload = {
  imageFigureId: string;
};

export type ImageImportStartedPayload = {
  imageFigureId: string;
};

export type ImageImportFinishedPayload = {
  jsonFile: Record<any, any>;
  imageFigureId: string;
};

type ImageImportFailedPayload = {
  imageFigureId: string;
};

export type ImageSpinnerPositionsChangedPayload = {
  imgNodeRects: ImgNodeRectsType;
};

export type TextValueReplacedPayload = {
  textNodeId: string;
  value: string;
  parsedSelection?: ParsedSelectionType | null;
  allowCmsBoundRichTextNativeRender?: boolean;
};

export type ClientRectUpdatedPayload =
  | {
      rangeClientRect: Box | null | undefined;
    }
  | {
      nodeClientRect: Box | null | undefined;
    };

export type LinkModeStartedPayload = {
  linkOnlyMode: boolean;
  parsedSelection?: ParsedSelectionType | null;
  subtree: ExpressionElement;
  collectionId?: string;
  itemId?: string;
};

export type linkToolbarChangesMadePayload = LinkToolbarChanges;

export type ContentEditorSaveEmbedPayload = {
  value: string;
};

export type embedEditorOpenedPayload = {
  id: string;
  recordType: 'item' | 'embed';
  initialText: string;
};

export type dynFieldModifiedOnCanvasPayload = {
  item: CollectionItem;
  field: FieldType;
  value: string;
  hasError: boolean;
};

// Creator types
export type SelectionUpdatedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_SELECTION_UPDATED,
  SelectionUpdatedPayload
>;

export type FormatToggledActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_FORMAT_TOGGLED,
  FormatToggledPayload
>;

export type CharsInsertedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_CHARS_INSERTED,
  CharsInsertedPayload
>;

export type MouseDownActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_MOUSE_DOWN,
  MouseDownPayload
>;

export type MouseUpActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_MOUSE_UP,
  MouseUpPayload
>;

export type CutActionCreator = ActionCreator<typeof CONTENT_EDITOR_CUT, SeedId>;

export type PasteActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_PASTE,
  PastePayload
>;

export type KeyDownActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_KEY_DOWN,
  KeyDownPayload
>;

export type KeyboardShortcutPressedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_KEYBOARD_SHORTCUT_PRESSED,
  KeyboardShortcutPressedPayload
>;

export type TextShortcutActivatedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_TEXT_SHORTCUT_ACTIVATED,
  TextShortcutActivatedPayload
>;

export type MarkdownFormattingActivatedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_MARKDOWN_FORMATTING_ACTIVATED,
  MarkdownFormattingActivatedPayload
>;

export type FigureAlignmentChangedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_FIGURE_ALIGNMENT_CHANGED,
  FigureAlignmentChangedPayload
>;

export type FigureSettingsOpenedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_FIGURE_SETTINGS_OPENED,
  void
>;

export type FigureAlignedAndHeightUpdatedFromSettingsActionCreator =
  ActionCreator<
    typeof CONTENT_EDITOR_FIGURE_ALIGNED_HEIGHT_UPDATED_FROM_SETTINGS,
    FigureAlignedAndHeightUpdatedFromSettingsPayload
  >;

export type AltTextUpdatedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_FIGURE_ALT_TEXT_UPDATED,
  AltTextUpdatedPayload
>;

export type MaxWidthUpdatedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_FIGURE_MAX_WIDTH_UPDATED,
  MaxWidthUpdatedPayload
>;

export type MaxWidthHeightUpdatedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_FIGURE_MAX_WIDTH_HEIGHT_UPDATED,
  MaxWidthHeightUpdatedPayload
>;

export type FigureLinkUpdatedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_FIGURE_LINK_UPDATED,
  FigureLinkUpdatedPayload
>;

export type LinkCancelledActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_LINK_CANCELLED,
  SeedId
>;

export type LinkRemovedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_LINK_REMOVED,
  LinkRemovedPayload
>;

export type LinkAcceptedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_LINK_ACCEPTED,
  SeedId
>;

export type LinkToolbarSavedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_LINK_TOOLBAR_SAVED,
  SeedId
>;

export type VideoCancelledActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_VIDEO_CANCELLED,
  void
>;
export type CustomClassAcceptedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_CUSTOM_CLASS_ACCEPTED,
  CustomClassAcceptedPayload
>;
export type CustomClassCancelledActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_CUSTOM_CLASS_CANCELLED,
  void
>;
export type CustomClassRemovedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_CUSTOM_CLASS_REMOVED,
  void
>;

export type ImageUploadProgressChangedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_IMAGE_UPLOAD_PROGRESS_CHANGED,
  ImageUploadProgressChangedPayload
>;

export type ImagePreviewSetActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_IMAGE_PREVIEW_SET,
  ImagePreviewSetPayload
>;

export type ImageUploadFinishedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_IMAGE_UPLOAD_FINISHED,
  ImageUploadFinishedPayload
>;

export type ImageUploadedWithErrorActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_IMAGE_UPLOAD_ERRORED,
  ImageUploadedWithErrorPayload
>;

export type ImageUploadFailedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_IMAGE_UPLOAD_FAILED,
  ImageUploadFailedPayload
>;

export type ImageReplacementStartedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_IMAGE_REPLACEMENT_STARTED,
  ImageReplacementStartedPayload
>;

export type ImageReplacementSucceededActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_IMAGE_REPLACEMENT_SUCCEEDED,
  ImageReplacementSucceededPayload
>;

export type ImageReplacementFailedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_IMAGE_REPLACEMENT_FAILED,
  ImageReplacementFailedPayload
>;

export type ImageImportStartedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_IMAGE_IMPORT_STARTED,
  ImageImportStartedPayload
>;

export type ImageImportFinishedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_IMAGE_IMPORT_FINISHED,
  ImageImportFinishedPayload
>;

export type ImageImportFailedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_IMAGE_IMPORT_FAILED,
  ImageImportFailedPayload
>;

export type ImageSpinnerPositionsChangedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_IMAGE_SPINNER_POSITIONS_CHANGED,
  ImageSpinnerPositionsChangedPayload
>;

export type TextValueReplacedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_TEXT_VALUE_REPLACED,
  TextValueReplacedPayload
>;

export type ClientRectUpdatedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_SELECTION_RECT_UPDATED,
  ClientRectUpdatedPayload
>;

export type LinkModeStartedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_LINK_MODE_STARTED,
  LinkModeStartedPayload
>;

export type UndoActionCreator = ActionCreator<typeof CONTENT_EDITOR_UNDO, void>;

export type RedoActionCreator = ActionCreator<typeof CONTENT_EDITOR_REDO, void>;

export type linkToolbarChangesMadeActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_LINK_TOOLBAR_CHANGES_MADE,
  linkToolbarChangesMadePayload
>;

export type TextOfNodeSelectedInInstanceEditingEndedActionCreator =
  ActionCreator<typeof TEXT_OF_NODE_SELECTED_IN_INSTANCE_EDITING_ENDED, void>;

type ContentEditorSaveEmbedCreator = ActionCreator<
  typeof CONTENT_EDITOR_EMBED_TEXT_SAVED,
  ContentEditorSaveEmbedPayload
>;

export type ContentEditorOpenCodeBlockSettingsCreator = ActionCreator<
  typeof CONTENT_EDITOR_CODE_BLOCK_SETTINGS_OPENED,
  void
>;

export type ContentEditorOpenComponentSettingsCreator = ActionCreator<
  typeof CONTENT_EDITOR_COMPONENT_SETTINGS_OPENED,
  void
>;

export type DynFieldModifiedOnCanvasActionCreator = ActionCreator<
  typeof DYN_FIELD_MODIFIED_ON_CANVAS,
  dynFieldModifiedOnCanvasPayload
>;

export type ContentEditorValidationErrorShownActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_VALIDATION_ERROR_SHOWN,
  {
    error: any;
  }
>;

export type ContentEditorModalOpenedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_DISCARD_MODAL_OPENED,
  void
>;

export type ContentEditorModalClosedActionCreator = ActionCreator<
  typeof CONTENT_EDITOR_DISCARD_MODAL_CLOSED,
  void
>;

export type OnCanvasSaveStartedActionCreator = ActionCreator<
  typeof DYN_FIELD_SAVE_ON_CANVAS_STARTED,
  void
>;

export type OnCanvasSaveSuccessActionCreator = ActionCreator<
  typeof DYN_FIELD_SAVE_ON_CANVAS_SUCCESS,
  void
>;

export type DynFieldSaveOnCanvasErrorActionCreator = ActionCreator<
  typeof DYN_FIELD_SAVE_ON_CANVAS_ERROR,
  {
    fieldSlug: string;
    errorPayload: OnCanvasSaveErrorPayload;
  }
>;

export type OnCanvasSaveDiscardedActionCreator = ActionCreator<
  typeof DYN_FIELD_SAVE_ON_CANVAS_DISCARDED,
  void
>;

export type RichTextContentModalEditStarted = ActionCreator<
  typeof RICH_TEXT_CONTENT_MODAL_EDIT_STARTED,
  {
    subtree: ExpressionElement;
    guid: string;
    // When set, the modal Content Editor is seeded with this CMS target so
    // its save path can persist back to CMS. The reducer derives
    // `collectionId` / `itemId` from `item`.
    item?: CollectionItem | null;
    field?: FieldType | null;
  }
>;

// Creators
export const selectionUpdated: SelectionUpdatedActionCreator = createAction(
  CONTENT_EDITOR_SELECTION_UPDATED
);

export const linkToolbarChangesMade: linkToolbarChangesMadeActionCreator =
  createAction(CONTENT_EDITOR_LINK_TOOLBAR_CHANGES_MADE);

export const formatToggled: FormatToggledActionCreator = createAction(
  CONTENT_EDITOR_FORMAT_TOGGLED
);

export const charsInserted: CharsInsertedActionCreator = createAction(
  CONTENT_EDITOR_CHARS_INSERTED
);

export const mouseDown: MouseDownActionCreator = createAction(
  CONTENT_EDITOR_MOUSE_DOWN
);

export const mouseUp: MouseUpActionCreator = createAction(
  CONTENT_EDITOR_MOUSE_UP
);

export const cut: CutActionCreator = createAction(CONTENT_EDITOR_CUT);

export const paste: PasteActionCreator = createAction(CONTENT_EDITOR_PASTE);

export const keyDown: KeyDownActionCreator = createAction(
  CONTENT_EDITOR_KEY_DOWN
);

export const keyboardShortcutPressed: KeyboardShortcutPressedActionCreator =
  createAction(CONTENT_EDITOR_KEYBOARD_SHORTCUT_PRESSED);

export const textShortcutActivated: TextShortcutActivatedActionCreator =
  createAction(CONTENT_EDITOR_TEXT_SHORTCUT_ACTIVATED);

export const markdownFormattingActivated: MarkdownFormattingActivatedActionCreator =
  createAction(CONTENT_EDITOR_MARKDOWN_FORMATTING_ACTIVATED);

export const figureSettingsOpened: FigureSettingsOpenedActionCreator =
  createAction(CONTENT_EDITOR_FIGURE_SETTINGS_OPENED);

export const figureAlignmentChanged: FigureAlignmentChangedActionCreator =
  createAction(CONTENT_EDITOR_FIGURE_ALIGNMENT_CHANGED);

export const figureAlignedAndHeightUpdatedFromSettings: FigureAlignedAndHeightUpdatedFromSettingsActionCreator =
  createAction(CONTENT_EDITOR_FIGURE_ALIGNED_HEIGHT_UPDATED_FROM_SETTINGS);

export const altTextUpdated: AltTextUpdatedActionCreator = createAction(
  CONTENT_EDITOR_FIGURE_ALT_TEXT_UPDATED
);

export const maxWidthUpdated: MaxWidthUpdatedActionCreator = createAction(
  CONTENT_EDITOR_FIGURE_MAX_WIDTH_UPDATED
);

export const maxWidthHeightUpdated: MaxWidthHeightUpdatedActionCreator =
  createAction(CONTENT_EDITOR_FIGURE_MAX_WIDTH_HEIGHT_UPDATED);

export const figureLinkUpdated: FigureLinkUpdatedActionCreator = createAction(
  CONTENT_EDITOR_FIGURE_LINK_UPDATED
);

export const linkCancelled: LinkCancelledActionCreator = createAction(
  CONTENT_EDITOR_LINK_CANCELLED
);

export const linkRemoved: LinkRemovedActionCreator = createAction(
  CONTENT_EDITOR_LINK_REMOVED
);

export const linkAccepted: LinkAcceptedActionCreator = createAction(
  CONTENT_EDITOR_LINK_ACCEPTED
);
export const linkToolbarSaved: LinkToolbarSavedActionCreator = createAction(
  CONTENT_EDITOR_LINK_TOOLBAR_SAVED
);

export const videoCancelled: VideoCancelledActionCreator = createAction(
  CONTENT_EDITOR_VIDEO_CANCELLED
);

export const customClassAccepted: CustomClassAcceptedActionCreator =
  createAction(CONTENT_EDITOR_CUSTOM_CLASS_ACCEPTED);

export const customClassCancelled: CustomClassCancelledActionCreator =
  createAction(CONTENT_EDITOR_CUSTOM_CLASS_CANCELLED);

export const customClassRemoved: CustomClassRemovedActionCreator = createAction(
  CONTENT_EDITOR_CUSTOM_CLASS_REMOVED
);

export const imageUploadProgressChanged: ImageUploadProgressChangedActionCreator =
  createAction(CONTENT_EDITOR_IMAGE_UPLOAD_PROGRESS_CHANGED);

export const imagePreviewSet: ImagePreviewSetActionCreator = createAction(
  CONTENT_EDITOR_IMAGE_PREVIEW_SET
);

export const imageUploadFinished: ImageUploadFinishedActionCreator =
  createAction(CONTENT_EDITOR_IMAGE_UPLOAD_FINISHED);

export const imageUploadedWithError: ImageUploadedWithErrorActionCreator =
  createAction(CONTENT_EDITOR_IMAGE_UPLOAD_ERRORED);

export const imageUploadFailed: ImageUploadFailedActionCreator = createAction(
  CONTENT_EDITOR_IMAGE_UPLOAD_FAILED
);

export const imageReplacementStarted: ImageReplacementStartedActionCreator =
  createAction(CONTENT_EDITOR_IMAGE_REPLACEMENT_STARTED);

export const imageReplacementSucceeded: ImageReplacementSucceededActionCreator =
  createAction(CONTENT_EDITOR_IMAGE_REPLACEMENT_SUCCEEDED);

export const imageReplacementFailed: ImageReplacementFailedActionCreator =
  createAction(CONTENT_EDITOR_IMAGE_REPLACEMENT_FAILED);

export const imageImportStarted: ImageImportStartedActionCreator = createAction(
  CONTENT_EDITOR_IMAGE_IMPORT_STARTED
);

export const imageImportFinished: ImageImportFinishedActionCreator =
  createAction(CONTENT_EDITOR_IMAGE_IMPORT_FINISHED);

export const imageImportFailed: ImageImportFailedActionCreator = createAction(
  CONTENT_EDITOR_IMAGE_IMPORT_FAILED
);

export const imageSpinnerPositionsChanged: ImageSpinnerPositionsChangedActionCreator =
  createAction(CONTENT_EDITOR_IMAGE_SPINNER_POSITIONS_CHANGED);

export const textValueReplaced: TextValueReplacedActionCreator = createAction(
  CONTENT_EDITOR_TEXT_VALUE_REPLACED
);

export const clientRectUpdated: ClientRectUpdatedActionCreator = createAction(
  CONTENT_EDITOR_SELECTION_RECT_UPDATED
);

export const linkModeStarted: LinkModeStartedActionCreator = createAction(
  CONTENT_EDITOR_LINK_MODE_STARTED
);

export const undo: UndoActionCreator = createAction(CONTENT_EDITOR_UNDO);

export const redo: RedoActionCreator = createAction(CONTENT_EDITOR_REDO);

export const contentEditorSaveEmbed: ContentEditorSaveEmbedCreator =
  createAction(CONTENT_EDITOR_EMBED_TEXT_SAVED);

export const textOfNodeSelectedInInstanceEditingEnded: TextOfNodeSelectedInInstanceEditingEndedActionCreator =
  createAction(TEXT_OF_NODE_SELECTED_IN_INSTANCE_EDITING_ENDED);

export const openCodeBlockSettings: ContentEditorOpenCodeBlockSettingsCreator =
  createAction(CONTENT_EDITOR_CODE_BLOCK_SETTINGS_OPENED);

export const openComponentSettings: ContentEditorOpenComponentSettingsCreator =
  createAction(CONTENT_EDITOR_COMPONENT_SETTINGS_OPENED);

export const dynFieldModifiedOnCanvas: DynFieldModifiedOnCanvasActionCreator =
  createAction(DYN_FIELD_MODIFIED_ON_CANVAS);

export const contentEditorValidationErrorShown: ContentEditorValidationErrorShownActionCreator =
  createAction(CONTENT_EDITOR_VALIDATION_ERROR_SHOWN);

export const contentEditorModalOpened: ContentEditorModalOpenedActionCreator =
  createAction(CONTENT_EDITOR_DISCARD_MODAL_OPENED);

export const contentEditorModalClosed: ContentEditorModalClosedActionCreator =
  createAction(CONTENT_EDITOR_DISCARD_MODAL_CLOSED);

export const startOnCanvasEditSave: OnCanvasSaveStartedActionCreator =
  createAction(DYN_FIELD_SAVE_ON_CANVAS_STARTED);

export const dynFieldSaveOnCanvasError: DynFieldSaveOnCanvasErrorActionCreator =
  createAction(DYN_FIELD_SAVE_ON_CANVAS_ERROR);

export const successOnCanvasEditSave: OnCanvasSaveSuccessActionCreator =
  createAction(DYN_FIELD_SAVE_ON_CANVAS_SUCCESS);

export const discardOnCanvasEditSave: OnCanvasSaveDiscardedActionCreator =
  createAction(DYN_FIELD_SAVE_ON_CANVAS_DISCARDED);

export const richTextContentModalEditStarted: RichTextContentModalEditStarted =
  createAction(RICH_TEXT_CONTENT_MODAL_EDIT_STARTED);
